<template>
  <PCHeaderBar />

  <div class="pc__store-page">
    <div class="store-header">
      <div class="store-card">
        <div class="store-icon">
          <a :href="fullStoreUrl"><img :src="store.storeIcon" alt=""></a>
        </div>
        <div class="info-wrapper">
          <h1 class="store-title">
            <a :href="fullStoreUrl"><span>{{ store.name }}</span></a>
          </h1>
          <div class="mark-box">
            <img v-if="store.authType === 1" class="store-tag" :src="$cvWebpFn(OFFICIAL_STORE_BADGE_URL)" alt="Brand Official" />
            <img v-else-if="store?.authType === 2" class="store-tag" :src="$cvWebpFn(AUTHORIZED_STORE_BADGE_URL)" alt="Brand Authorized" />
            <img v-if="store.tags?.includes('store_preferred')" class="store-tag" :src="$cvWebpFn(PREFERRED_STORE_BADGE_URL)" alt="Preferred Store" />
          </div>
          
          <p class="score-info">
            <span class="r-info-label">Score:</span>
            <span class="r-info-value">
              <span class="number">{{ storeCommentStat.score }} </span>
              <span>/ 5.0</span>
              <span class="value">
                ({{ storeCommentStat.commentNum }} Ratings)
              </span>
            </span>
            <span class="item-border">Products:</span>
            <span class="number"><span class="value">{{ store.products }}</span></span>
            <span class="item-border">Followers:</span>
            <span class="number"><span class="value">{{ formatNumber(store.follows)}}</span></span>
          </p>
        </div>
      </div>

      <div class="store-reviews-card">
        <van-button
          v-if="store.isFollow"
          class="button-default cancel"
          size="small"
          @click="emits('follow:click')"
        >
          Followed
        </van-button>
        <van-button
          v-else="store.isFollow"
          class="button-default primary"
          size="small"
          @click="emits('follow:click')"
        >
          + Follow
        </van-button>
        <van-button
          size="small"
          class="button-default"
          @click="emits('chat:click')"
        >
          Chat
        </van-button>
      </div>
    </div>

    <div class="vouchers" v-if="voucherList.length > 0">
      <div
        v-for="(item, index) in voucherList"
        :key="index"
        class="voucher-item"
      >
        <VoucherItemV2 :item="item" show-type="store" receive-scene="SHOP_PAGE" @callback="handleGetVoucherCallback(item)" />
      </div>
    </div>

    <div class="tabs">
      <div class="store-category">
        <p class="ctg-title">Store Category</p>
        <van-cell
          v-for="(item, idx) in categoryList"
          is-link
          :key="idx"
          :title="`${item.name} (${item.products})`"
          @click="handleCtgClick(item)"
        />
      </div>

      <div class="listings">
        <div class="tab-title-bar">
          <div class="tab-title product" :select="active === 'product' ? '1' : '0'" @click="handleTabChange('product')">All products</div>
          <div class="tab-title category" :select="active === 'category' ? '1' : '0'" @click="handleTabChange('category')">Recommended</div>
          <div class="store-search-div">
            <van-search
              v-model="storeName"
              show-action
              :clearable="false"
              placeholder="Search In Store"
              class="store-search-input"
              @search="emits('search:click', storeName)"
            >
              <template #action>
                <van-icon
                  class="serch-icon"
                  name="search"
                  @click="emits('search:click', storeName)"
                />
              </template>
            </van-search>
          </div>
        </div>

        <div class="tab-wrapper">
          <div class="tab-content" v-show="active === 'product'">
            <div class="store-listing">
              <div
                v-for="item in storeListings"
                :key="item.listingId"
                class="listing-item"
              >
                <PCProductItemCard :product-data="item" type="normal" />
              </div>
            </div>

            <div class="pagination-box">
              <ClientOnly>
                <van-pagination
                  :model-value="pagination.currentPage"
                  :page-count="pagination.maxPage"
                  :items-per-page="20"
                  @change="e => emits('page:change', e)"
                />
              </ClientOnly>
            </div>
          </div>
          <div class="tab-content" v-show="active === 'category'">
            <div class="store-listing">
              <div
                v-for="item in storeRecommendListings"
                :key="item.listingId"
                class="listing-item"
              >
                <PCProductItemCard :product-data="item" type="default" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <PCFooter />
</template>

<script setup lang="ts">
import { search } from '~/api/models/system';
import type { Voucher } from '~/api/models/userInfo';
type TabType = 'home' | 'product' | 'category' | 'chat'
const OFFICIAL_STORE_BADGE_URL = 'https://image.kilimall.com/c/h5/ic_official_store.png'
const AUTHORIZED_STORE_BADGE_URL = 'https://image.kilimall.com/c/h5/ic_authorized_store.png'
const PREFERRED_STORE_BADGE_URL = 'https://image.kilimall.com/c/h5/ic_preferred_storex.png'
interface IProps {
  active: TabType;
  store: any;
  storeCommentStat: { score: string, commentNum: string },
  voucherList: any,
  categoryList: any[],
  storeListings: any[],
  pagination: { currentPage: number, maxPage: number } 
  storeRecommendListings: any[]
}
const props = defineProps<IProps>()
const emits = defineEmits<{
  (e: 'page:change', v: number):void,
  (e: 'update:active', v: TabType): void,
  (e:'ctg:click', v: any): void
  (e: 'follow:click'): void,
  (e: 'chat:click'): void
  (e: 'search:click', v: string): void
}>()

const storeName = ref('')
const handleTabChange = (e: TabType) => {
  emits('update:active', e)
}

const handleCtgClick = (item: any) => {
  emits('ctg:click', item)
}
const handleGetVoucherCallback = (item: Voucher) => {
  item.isEarned = true
}
const formatNumber = (value: number) => {
  if (value < 1000) {
    return value.toString(); // Numbers less than 1000
  } else if (value >= 1000 && value < 10000) {
    return value.toLocaleString(); // Numbers between 1000 and 9999
  } else if (value >= 10000 && value < 100000) {
    return (value / 1000).toFixed(1) + 'k'; // 10000 to 99999
  } else {
    return (value / 10000).toFixed(1) + 'w'; // 100000 and above
  }
}

const fullStoreUrl = ref<string>('')
onMounted(() => {
  const search = new URLSearchParams(location.search)
  search.delete('storeid')
  search.append('storeid', props.store.id)
  fullStoreUrl.value = location.pathname + '?' + search.toString()
})
</script>

<style lang="scss" scoped>
.pc__store-page{
  width: var(--viewport-width);
  margin: 0 auto;
}
.mark-box{
  display: flex;
  align-items: center;
  gap: 10px;
}
.store-tag {
  display: block;
  width: auto;
  height: 32px;
  margin-top: 5px;
}
.store-header{
  padding: 40px;
  background-color: #fff;
  margin-top: 40px;
  display: flex;
  align-items: center;
}
.store-card {
  display: flex;
  width: 1400px;
  border-radius: 10px;
  .store-icon{
    border: 1px solid #CCCCCC;
    overflow: hidden;
    min-width: 150px;
    background-color: #fff;
    padding: 2px;
    width: 150px;
    height: 150px;
    img{
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }
  .info-wrapper{
    flex: 1;
    margin-left: 40px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    .score-info {
      margin-top: 20px;
      font-size: 36px;
      color: #333333;
      .number {
        color: #DD3131;
        margin: 0 10px;
      }
      .item-border {
        margin: 0 10px;
        &::before {
          display: inline-block;
          content: ' ';
          width: 2px;;
          height: 30px;
          margin: 0 auto;
          margin-right: 20px;
          border-left: 2px solid #DBDBDB;
        }
      }
    }
    .store-title{
      font-weight: 600;
      font-size: 48px;
      @include ellipsis();
    }
    .followers{
      margin-top: 20px;
    }
    .options{
      display: flex;
      margin-top: 10px;
      .btn{
        border: 1px solid #fff;
        color: #fff;
        width: 320px;
        border-radius: 10px;
        text-align: center;
        padding: 10px 10px;
        margin-right: 30px;
        cursor: pointer;
        user-select: none;
      }
      .follow{
        background-color: #fff;
        color: var(--pc-default-color);
      }
    }
  }

}

.store-reviews-card{
  margin-left: 60px;
  flex: 1;
  text-align: right;
  .button-default {
    width: 240px;
    height: 80px;
    border-radius: 0;
    color: #333333;
    font-size: 36px;
    border-color: #999999;
    margin-left: 40px;
  }
  .button-default.primary {
    background-color: #DD3132;
    color: white;
    border-color: #DD3132;
  }
  .button-default.cancel {
    background-color: #E9E9E9;
    color: #999999;
    border-color: #E9E9E9;
  }
  .review-info{
    line-height: 100px;
    display: flex;
    align-items: center;
    .r-info-label{
      width: 180px;
      display: inline-block;
    }
    .r-info-value{
      display: flex;
      align-items: center;
      .value{
        margin-left: 12px;
        color: var(--pc-default-color);
      }
    }
  }
}
.vouchers{
  margin-top: 30px;
  display: flex;
  padding: 15px;
  flex-wrap: nowrap;
  overflow-x: auto;
  background-color: #fff;
  @include pc__scrollbar();
  .voucher-item{
    margin-right: 15px;
    min-width: 33%;
  }
  .voucher-item:deep(.voucher-v2-item) {
    margin-bottom: 0;
  }
}

.tabs{
  display: flex;
  margin: 30px 0 60px 0;
  .store-category{
    min-width: 600px;
    width: 600px;
    background-color: #fff;
    padding: 30px 0;
    .ctg-title{
      font-size: 36px;
      font-weight: 600;
      text-shadow: 0 0 1px #cacaca;
      padding-left: 40px;
      margin-bottom: 20px;
    }
    .van-cell {
      color: #666666;
      font-size: 36px;
      padding: 30px 40px;
    }
  }
  .listings{
    margin-left: 30px;
    flex: 1;
    
    .store-listing{
      display: flex;
      flex-wrap: wrap;
      max-width: calc(var(--viewport-width) - 600px - 10px);
      margin: 20px -8px 0 -8px;
      .listing-item{
        width: 25%;
        padding: 10px;
        .product-item {
          border-radius: 0;
          padding: 26px;
        }
      }
    }

    .tab-title-bar{
      display: flex;
      background-color: #fff;
      align-items: center;
      height: 128px;
      .tab-title{
        // width: 180px;
        height: 100px;
        line-height: 100px;
        padding: 0 60px;
        cursor: pointer;
        text-align: center;
        font-size: 32px;
        transition: all .2s;
        width: 360px;
      }
      .tab-title.category {
        width: 360px;
      }

      .tab-title[select="1"] {
        color: var(--pc-default-color);
        font-weight: 600;
        &::after {
          display: block;
          content: ' ';
          width: 118px;
          margin: 0 auto;
          border-bottom: 4px solid #DD3132;
          margin-top: 10px;
        }
      }
      .store-search-div {
        margin-left: 460px;
        width: 590px;
        text-align: right;
        .store-search-input {
          width: 560px;
          height: 70px;
          :deep() .van-search__content {
            border: 1px solid #999999;
            background-color: white;
            .van-field__left-icon {
              display: none;
            }
          }
          :deep() .van-search__action {
            background-color: #DD3132;
            color: white;
            border: 1px solid #DD3132;
            margin-left: -2px;
            height: 70px;
            width: 80px;
            text-align: center;
          }
          .serch-icon {
            font-size: 58px;
            margin-left: -5px;
            margin-top: 5px;
          }
        }
      }
    }
    .tab-wrapper{
      display: flex;
      .tab-content{
        flex: 1;
      }
    }
  }
}
.pagination-box{
  margin-top: 20px;
}
</style>
